/**
 * API Client Service
 * Centralized HTTP client with error handling, retries, and type safety
 * Now using the advanced ApiManager for better reusability
 */

import { config } from '../utils/config';
import { apiManager } from './apiManager';
import type {
    ContactFormData,
    ReferralFormData,
    NewsletterFormData,
    UpsertReferralRequest,
    UpsertReferralResponse
} from '../types';

// API Response Types (keeping for backward compatibility)
export interface ApiResponse<T = unknown> {
    success: boolean;
    data?: T;
    message?: string;
    error?: string;
}

export class ApiError extends Error {
    status?: number;
    code?: string;

    constructor({ message, status, code }: { message: string; status?: number; code?: string }) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.code = code;
    }
}

// HTTP Client Class
class HttpClient {
    private baseURL: string;
    private defaultHeaders: Record<string, string>;

    constructor(baseURL: string) {
        this.baseURL = baseURL;
        this.defaultHeaders = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<ApiResponse<T>> {
        const url = `${this.baseURL}${endpoint}`;

        const config: RequestInit = {
            ...options,
            headers: {
                ...this.defaultHeaders,
                ...options.headers,
            },
        };

        try {
            const response = await fetch(url, config);

            // Handle HTTP errors
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new ApiError({
                    message: errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                    status: response.status,
                    code: errorData.code,
                });
            }

            const data = await response.json();
            return {
                success: true,
                data,
                message: data.message,
            };
        } catch (error) {
            console.error('API Request failed: - api.ts:84', error);

            if (error instanceof Error) {
                return {
                    success: false,
                    error: error.message,
                };
            }

            return {
                success: false,
                error: 'Network error occurred',
            };
        }
    }

    async get<T>(endpoint: string): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { method: 'GET' });
    }

    async post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined,
        });
    }

    async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
        return this.request<T>(endpoint, { method: 'DELETE' });
    }
}

// Create HTTP client instance
const httpClient = new HttpClient(config.API_BASE_URL);

// API Service Functions
export const apiService = {
    // Contact Form Submission
    async submitContactForm(data: ContactFormData): Promise<ApiResponse> {
        if (!config.ENABLE_CONTACT_FORM) {
            return {
                success: false,
                error: 'Contact form is currently disabled',
            };
        }

        return httpClient.post('/contact', data);
    },

    // Newsletter Subscription
    async subscribeNewsletter(data: NewsletterFormData): Promise<ApiResponse> {
        return httpClient.post('/newsletter/subscribe', data);
    },

    // Referral System
    async submitReferral(data: ReferralFormData): Promise<ApiResponse> {
        if (!config.ENABLE_REFERRAL_SYSTEM) {
            return {
                success: false,
                error: 'Referral system is currently disabled',
            };
        }

        return httpClient.post('/referral/register', data);
    },

    async validateReferralCode(code: string): Promise<ApiResponse<{ valid: boolean; details?: unknown }>> {
        if (!config.ENABLE_REFERRAL_SYSTEM) {
            return {
                success: false,
                error: 'Referral system is currently disabled',
            };
        }

        return httpClient.get(`/referral/validate/${encodeURIComponent(code)}`);
    },

    /**
     * Upsert Referral - Create or update a referral
     * POST /client/referral/upsertReferral
     * @param referralCode - The referral code from the referring user
     * @param toEmailId - The email of the person being referred
     * @returns Promise with upsert result
     */
    async upsertReferral(data: UpsertReferralRequest): Promise<ApiResponse<UpsertReferralResponse>> {
        if (!config.ENABLE_REFERRAL_SYSTEM) {
            return {
                success: false,
                error: 'Referral system is currently disabled',
            };
        }

        // Validate required fields
        if (!data.referralCode || !data.toEmailId) {
            return {
                success: false,
                error: 'referralCode and toEmailId are required',
            };
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(data.toEmailId)) {
            return {
                success: false,
                error: 'Invalid email format',
            };
        }

        // Use apiManager
        return apiManager.post<UpsertReferralResponse>(
            '/client/referral/upsertReferral',
            {
                referralCode: data.referralCode.trim(),
                toEmailId: data.toEmailId.trim().toLowerCase(),
            },
            {
                retry: 2, // Retry twice on failure
                timeout: 10000, // 10 second timeout
            }
        );
    }, async validateEmail(email: string): Promise<ApiResponse<{ valid: boolean; available: boolean }>> {
        return httpClient.post('/auth/validate-email', { email });
    },

    // Analytics
    // Analytics
    async trackEvent(event: string, properties?: Record<string, unknown>): Promise<ApiResponse> {
        if (!config.ENABLE_ANALYTICS) {
            return { success: true, message: 'Analytics disabled' };
        }

        return httpClient.post('/analytics/track', {
            event,
            properties,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
        });
    },

    // App downloads tracking
    async trackDownload(platform: 'ios' | 'android', source?: string): Promise<ApiResponse> {
        return this.trackEvent('app_download_click', {
            platform,
            source: source || 'landing_page',
        });
    },

    // Health check
    async healthCheck(): Promise<ApiResponse> {
        return httpClient.get('/health');
    },
};